import { useMemo } from "react";
import { useMeta } from "@/api/queries";

/**
 * Value formatting (PRD FR-6.3).
 *
 * The server decides what a column *is* — currency, percent, count — from its
 * name and its values, and sends that as a hint beside untouched rows. This
 * module is the other half: turning a raw number into the string a reader sees,
 * in the deployment's locale and currency.
 *
 * Locale and currency come from `/api/meta`, not from a constant here. A
 * deployment outside the dollar zone is a settings change, and hardcoding "$"
 * would make it a code change — the same reason no component carries a colour.
 */

export type ValueFormat =
  | "currency"
  | "percent"
  | "percent_fraction"
  | "integer"
  | "number"
  | "date"
  | "text";

export interface Formatter {
  /** A cell or a tooltip: the full, grouped value. */
  value: (value: unknown, format?: ValueFormat) => string;
  /** An axis tick: the same value, shortened to K/M/B. */
  axis: (value: unknown, format?: ValueFormat) => string;
  locale: string;
  currency: string;
}

const EMPTY = "—";

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return null;
}

function decimals(value: number, max = 2): number {
  const size = Math.abs(value);
  if (size >= 1000 || Number.isInteger(value)) return 0;
  if (size >= 10) return Math.min(max, 1);
  return max;
}

export function buildFormatter(locale: string, currency: string): Formatter {
  const cache = new Map<string, Intl.NumberFormat>();
  const format = (kind: string, options: Intl.NumberFormatOptions) => {
    // Keyed on the options as well as the kind: the fraction digits depend on
    // the value, and a key without them let the first number formatted fix
    // the precision of every one after it — an axis whose first tick was 0
    // labelled 0.05, 0.10 and 0.15 all as "0".
    const key = `${kind}:${JSON.stringify(options)}`;
    let instance = cache.get(key);
    if (!instance) {
      // A currency this deployment mistyped must not take the page down with
      // it; plain numbers are a better failure than a blank screen.
      try {
        instance = new Intl.NumberFormat(locale, options);
      } catch {
        instance = new Intl.NumberFormat(undefined, { ...options, style: "decimal" });
      }
      cache.set(key, instance);
    }
    return instance;
  };

  function render(value: unknown, kind: ValueFormat = "text", compact = false): string {
    if (value === null || value === undefined || value === "") return EMPTY;
    if (kind === "text" || kind === "date") return String(value);

    const number = toNumber(value);
    if (number === null) return String(value);
    const notation = compact && Math.abs(number) >= 10_000 ? "compact" : "standard";

    switch (kind) {
      case "currency":
        return format(`c-${notation}`, {
          style: "currency",
          currency,
          notation,
          maximumFractionDigits: notation === "compact" ? 1 : decimals(number),
          minimumFractionDigits: 0,
        }).format(number);
      case "percent":
        return `${format("p", { maximumFractionDigits: 1 }).format(number)}%`;
      case "percent_fraction":
        return format("pf", { style: "percent", maximumFractionDigits: 1 }).format(number);
      case "integer":
        return format(`i-${notation}`, {
          notation,
          maximumFractionDigits: notation === "compact" ? 1 : 0,
        }).format(number);
      default:
        return format(`n-${notation}`, {
          notation,
          maximumFractionDigits: notation === "compact" ? 1 : decimals(number),
        }).format(number);
    }
  }

  return {
    value: (value, kind) => render(value, kind, false),
    axis: (value, kind) => render(value, kind, true),
    locale,
    currency,
  };
}

/** The deployment's formatter. Falls back to en-US/USD until `/api/meta` lands. */
export function useFormatter(): Formatter {
  const { data: meta } = useMeta();
  const locale = meta?.locale ?? "en-US";
  const currency = meta?.currency ?? "USD";
  return useMemo(() => buildFormatter(locale, currency), [locale, currency]);
}
